// Shared 401/418 status-dispatch for internal API responses. Both the typed
// client (`createApiClient`) and the realtime catch-up fetcher
// (`defaultSnapshotCatchupFetcher`) route a stale session (401) / banned
// account (418) through the SAME app-injected handlers — so the raw-fetch
// catch-up, a documented carve-out from the typed-client fetch ban, gets the
// identical routing without duplicating the status checks. The handlers are a
// fire-and-forget navigation side-channel: the Response is never consumed or
// mutated here.

/** App-injected side-channel handlers, keyed by HTTP status. */
export interface ResponseStatusHandlers {
    /**
     * Fired on **401** — no valid session. The app routes to login (with its
     * own believed-auth gate so anonymous probes that 401 by design don't
     * redirect). 403 (authenticated-but-forbidden) does NOT trigger it.
     */
    onUnauthorized?: (res: Response) => void;
    /**
     * Fired on **418** — the "account unavailable" sentinel a banned session
     * emits. The app routes to a dedicated account-unavailable screen instead
     * of the login bounce. A 418 can only come from a valid-but-banned
     * session, so no believed-auth gate is needed.
     */
    onAccountUnavailable?: (res: Response) => void;
}

/**
 * Fire the matching handler for a response's HTTP status: 401 →
 * `onUnauthorized`, 418 → `onAccountUnavailable`, every other status → nothing.
 * Best-effort — the Response is passed through untouched (a navigation
 * side-channel, not a swallow). Safe to call with a partial/empty handlers
 * object.
 *
 * @sideEffect Invokes the app-injected `onUnauthorized` / `onAccountUnavailable`
 *   callback, which typically navigates the browser.
 */
export function applyResponseStatusHandlers(
    res: Response,
    handlers: ResponseStatusHandlers,
): void {
    if (res.status === 401) handlers.onUnauthorized?.(res);
    if (res.status === 418) handlers.onAccountUnavailable?.(res);
}
